import React from 'react';
import styled from 'styled-components';
import { theme } from '@theme';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

interface UsageChartProps {
  data?: Array<{
    date: string;
    scorings: number;
    applications: number;
  }>;
  height?: number;
}

const ChartContainer = styled.div`
  width: 100%;
  background: ${theme.colors.white};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.lg};
  box-shadow: ${theme.shadows.md};
`;

const ChartTitle = styled.h3`
  font-size: ${theme.typography.sizes.lg};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.lg} 0;
`;

const CustomTooltip = styled.div`
  background: ${theme.colors.white};
  border: 1px solid ${theme.colors.surface};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.md};
  box-shadow: ${theme.shadows.lg};
`;

const TooltipLabel = styled.div`
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.xs};
`;

const TooltipItem = styled.div<{ color: string }>`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text}80;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  margin-top: ${theme.spacing.xs};

  &::before {
    content: '';
    width: 12px;
    height: 12px;
    background: ${props => props.color};
    border-radius: ${theme.borderRadius.sm};
  }
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 300px;
  color: ${theme.colors.text}60;
  font-size: ${theme.typography.sizes.base};
`;

const CustomTooltipComponent = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <CustomTooltip>
        <TooltipLabel>{label}</TooltipLabel>
        {payload.map((entry: any, index: number) => (
          <TooltipItem key={index} color={entry.color}>
            {entry.name}: {entry.value}
          </TooltipItem>
        ))}
      </CustomTooltip>
    );
  }
  return null;
};

// Mock data for demo
const defaultData = [
  { date: '1 Jan', scorings: 12, applications: 20 },
  { date: '5 Jan', scorings: 19, applications: 32 },
  { date: '10 Jan', scorings: 15, applications: 28 },
  { date: '15 Jan', scorings: 25, applications: 40 },
  { date: '20 Jan', scorings: 22, applications: 35 },
  { date: '25 Jan', scorings: 30, applications: 45 },
  { date: '30 Jan', scorings: 28, applications: 42 },
];

export const UsageChart: React.FC<UsageChartProps> = ({
  data = defaultData,
  height = 300,
}) => {
  if (!data || data.length === 0) {
    return (
      <ChartContainer>
        <ChartTitle>Activité récente</ChartTitle>
        <EmptyState>Aucune donnée disponible</EmptyState>
      </ChartContainer>
    );
  }

  return (
    <ChartContainer>
      <ChartTitle>Activité récente</ChartTitle>
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.surface} />
          <XAxis
            dataKey="date"
            stroke={theme.colors.text + '60'}
            style={{
              fontSize: theme.typography.sizes.xs,
              fontFamily: theme.typography.fontFamily,
            }}
          />
          <YAxis
            stroke={theme.colors.text + '60'}
            style={{
              fontSize: theme.typography.sizes.xs,
              fontFamily: theme.typography.fontFamily,
            }}
          />
          <Tooltip content={<CustomTooltipComponent />} />
          <Legend
            wrapperStyle={{
              fontSize: theme.typography.sizes.sm,
              fontFamily: theme.typography.fontFamily,
            }}
          />
          <Line
            type="monotone"
            dataKey="scorings"
            name="Scorings"
            stroke={theme.colors.primary}
            strokeWidth={2}
            dot={{ fill: theme.colors.primary, r: 4 }}
            activeDot={{ r: 6 }}
          />
          <Line
            type="monotone"
            dataKey="applications"
            name="Candidatures"
            stroke={theme.colors.accent}
            strokeWidth={2}
            dot={{ fill: theme.colors.accent, r: 4 }}
            activeDot={{ r: 6 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </ChartContainer>
  );
};
